import FavoriteBtn from "../../Assets/FavoriteBtn/FavoriteBtn"
import { useEffect, useMemo, useState } from "react"
import moment from "moment"
import Label from "../../Assets/Label/Label"
import { useAppSelector } from "../../../hooks"
import { selectUser } from "../../../redux/slice/auth"
import { selectArticles, toggleFavoriteArticles } from "../../../redux/slice/articles"
import { useGetArticleQuery, useSetFavoriteArticleMutation } from "../../../redux/api/articles"
import { useDispatch } from "react-redux"
import clsx from "clsx"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import TitleBack from "../../TitleBack/TitleBack"
import useWindowSize from "../../../hooks/useWindowSize"
import { setMobileMenuIsHidden } from "../../../redux/slice/isMoreModal"
import ArticlesAside from "../ArticlesAside/ArticlesAside"
import { GetUserAvatar } from "../../Assets/GetUserAvatar/GetUserAvatar"
import { getLocale } from "../../../utils/getLocale"
import { IArticle } from "../../../types/content"
import styles from "./Article.module.scss"

const Article = ({ id, project, isFavoriteLoading }: { id: string; project: string; isFavoriteLoading?: boolean }) => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const currentLang = getLocale()
  const dispatch = useDispatch()
  const user = useAppSelector(selectUser)
  const { favoriteArticles } = useAppSelector(selectArticles)
  const navigate = useNavigate()
  const { isDesktop } = useWindowSize()
  const [article, setArticle] = useState<IArticle | null>(null)

  const { data: articleData, isLoading: isArticleLoading, isFetching, error } = useGetArticleQuery({ id, project })
  const [setFav] = useSetFavoriteArticleMutation()

  useEffect(() => {
    setArticle(articleData || null)
  }, [articleData])

  const articleText = useMemo((): string => {
    if (!article?.text) return ""
    const text = article.text
      .replace("<table", `<div class="table-container"><table`)
      .replace("</table>", "</table></div>")
    return text
  }, [article])

  useEffect(() => {
    setFavorite(Boolean(favoriteArticles?.find((i) => i.id === id)))
  }, [favoriteArticles, id])

  const [isFavorite, setFavorite] = useState<boolean>(false)

  useEffect(() => {
    dispatch(setMobileMenuIsHidden(!user?.id))
  }, [id, user])

  useEffect(() => {
    if (error && error.data?.error === "No access") {
      navigate("/")
    }
  }, [error])

  const toggleFav = () => {
    if (!article) return
    try {
      setFavorite((prev) => !prev)
      setFav({ id: id, project_id: project }).then(() => {
        dispatch(toggleFavoriteArticles(article))
      })
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if (!user?.id || !id) return
    if (typeof window.Echo === "undefined") return
    const channelArticle = window.Echo.join(`articlesPublic.${id}`)
    const updateArticleBySocket = (event: IArticle) => {
      if (!event?.id) return
      setArticle((prev) => {
        return { ...prev, ...event }
      })
    }
    channelArticle?.listen(".updateArticlePublic", updateArticleBySocket)

    return () => {
      channelArticle?.stopListening(".updateArticlePublic", updateArticleBySocket)
    }
  }, [user, id])

  return isArticleLoading || isFetching ? (
    <>
      <div className={clsx("skeletonBlock", styles.titleLoading)} />
      <div className={clsx("skeletonBlock", styles.creatorLoading)} />
      <div className={clsx("skeletonBlock", styles.textLoading)} />
    </>
  ) : article ? (
    <div>
      {user && user.id && !isDesktop && (
        <TitleBack
          noLinkBack
          onClick={() => {
            navigate("/articles")
          }}
          title={t("knowledgeBase")}
          className={styles.titleBack}
        />
      )}
      <header className={styles.header}>
        {!isDesktop && <ArticlesAside />}
        <div className={styles.headerTop}>
          <h2 className={styles.title}>{article.name}</h2>
          {isDesktop && user?.id && !isFavoriteLoading && id && (
            <FavoriteBtn isActive={isFavorite} onClick={toggleFav} isDesktop />
          )}
        </div>
        {id && <ArticlesAside onlyChild={true} />}
        {user?.id && (
          <div className={styles.headerBot}>
            <GetUserAvatar
              avatar={article.creator?.avatar_id}
              name={article?.creator?.name}
              size={"xs"}
              className={styles.avatar}
            />
            <p>{article.creator?.name || t("unknownUser")}</p>

            <span className={styles.dot} />

            <p className={styles.date}>{moment(article.created_at).locale(currentLang).format("DD MMMM")}</p>

            <span className={styles.dot} />

            <span>
              {article.view_count} {t("views")}
            </span>
          </div>
        )}
        {!isDesktop && user?.id && !isFavoriteLoading && id && (
          <FavoriteBtn isActive={isFavorite} onClick={toggleFav} />
        )}
      </header>
      <div className={styles.article} dangerouslySetInnerHTML={{ __html: articleText }} />
      {article?.tags && article.tags.length > 0 ? (
        <div className={styles.artTags}>
          {article.tags.map((tag) => (
            <Label key={tag.id} txt={tag.name} mode={"gray"} />
          ))}
        </div>
      ) : null}
    </div>
  ) : null
}

export default Article
